import * as yup from "yup";

import type { Maybe } from "@/components/graphql/schema.graphql";
import {
  MAX_STYLE_WEIGHT,
  MIN_STYLE_WEIGHT,
} from "@/components/inference-forms/constants";
import {
  getPercentageFromWeight,
  getWeightFromPercentage,
} from "@/components/inference-forms/utils/utils";
import type { InferenceParameters } from "@/types";

import {
  STYLE_CAPABILITIES_FIELD_NAME,
  STYLE_COMPONENTS_FIELD_NAME,
  STYLE_FIELD_NAME,
} from "./constants";
import type { FormWithStyle } from "./types";

export const DEFAULT_FIELDS_STYLE = {
  [STYLE_FIELD_NAME]: null,
  [STYLE_CAPABILITIES_FIELD_NAME]: null,
  [STYLE_COMPONENTS_FIELD_NAME]: [],
};

const styleValidationSchema = yup
  .object()
  .shape({
    id: yup.string(),
    weight: yup
      .number()
      .typeError("Invalid value.")
      .min(MIN_STYLE_WEIGHT, `Weight must be at least ${MIN_STYLE_WEIGHT}.`)
      .max(MAX_STYLE_WEIGHT, `Weight must be at most ${MAX_STYLE_WEIGHT}.`),
  })
  .required("Model is required.");

export const validationSchemaWithStyle = {
  [STYLE_FIELD_NAME]: styleValidationSchema,
  [STYLE_COMPONENTS_FIELD_NAME]: yup.array().of(styleValidationSchema),
};

export const formatInferenceParamsForFormWithStyle = (
  params: Maybe<InferenceParameters>,
): Partial<FormWithStyle> => {
  const outputForm: Partial<FormWithStyle> = {};

  if (params.styles?.length) {
    outputForm[STYLE_FIELD_NAME] = {
      id: params.styles[0].id,
      weight: getPercentageFromWeight(params.styles[0].weight),
    };
    outputForm[STYLE_COMPONENTS_FIELD_NAME] = params.styles.map((style) => ({
      id: style.id,
      weight: getPercentageFromWeight(style.weight),
    }));
  }
  return outputForm;
};

export const formatFormWithStyleValuesForAPI = (
  inputForm: FormWithStyle,
): Partial<InferenceParameters> => {
  let components = inputForm[STYLE_COMPONENTS_FIELD_NAME] || [];
  const style = inputForm[STYLE_FIELD_NAME];
  if (components.length === 0 && style) {
    components.push(style);
  } else if (!components.find((c) => c.id === style?.id) && style) {
    components = [style];
  }
  return {
    styles: components.map((component) => ({
      id: component.id,
      weight: getWeightFromPercentage(component.weight) ?? 1,
    })),
  };
};
